import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { useQuickViewContext } from '~/quickView/components/QuickViewContext';
import { SignupMemberships } from '~/shared/constants/memberships';
import { ProductTag } from '~/shared/constants/products';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { isForcedSoldOut } from '~/shared/utils/isForcedSoldOut';
import { isPreorderEligibleBySelectedProduct } from '~/shared/utils/isPreorderEligibleBySelectedProduct';
import {
  getLocalStorageItemWithExpiry,
  setLocalStorageItemWithExpiry,
} from '~/shared/utils/localStorageHelper';
import { useMysteryBoxSelectionsContext } from '~/shared/utils/mysteryBoxSelections';
import useByoConfigForProduct from '~/shared/utils/useByoConfigForProduct';
import useGender from '~/shared/utils/useGender';
import useStoreLocation from '~/shared/utils/useStoreLocation';
import {
  useMembership,
  useAccountActions,
  useCustomer,
} from '~/techstyle-shared/react-accounts';
import {
  useCart,
  useCartActions,
  isEchoCartLineId,
} from '~/techstyle-shared/react-cart';
import {
  AttributeFieldTypes,
  useErrorContext,
  useProductAttributeFieldType,
  useProductContext,
  useProductDetailContext,
  useProductDetailSelectionState,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import {
  useDomain,
  useSession,
  useSessionActions,
  useSessionDetail,
} from '~/techstyle-shared/redux-core';

import { trackProductAdded, trackAddToCartError } from '../../actions';
import { getWarehouseInventory } from '../../utils/getPDPWarehouseInventory';
import useLoyaltyPricing from '../../utils/useLoyaltyPricing';

import errorMessages from './errorMessages';
import { useProductDetailEmbroideryContext } from './ProductDetailEmbroideryContext';

const vipCartViaMembershipProduct = config.get(
  'public.brand.features.vipCartViaMembershipProduct'
);

export const ProductDetailCTAErrorTypes = {
  NotLoggedInError: 'NotLoggedInError',
  SelectorError: 'SelectorError',
  APIError: 'APIError',
  BOPSDifferentStoreError: 'BOPSDifferentStoreError',
  SoldOutError: 'SoldOutError',
  BlacklistedWordError: 'BlacklistedWordError',
};

export const ShippingMethods = {
  ONLINE: 'online',
  BOPS: 'bops',
};

const PDPCTAContext = React.createContext();

export function useProductDetailCTAContext() {
  return useContext(PDPCTAContext);
}

const getProductId = (productDetailContext) => {
  const { selectionStateByProductIndex } = productDetailContext;
  return selectionStateByProductIndex[0].selectedSKU.productId;
};

const getBundleProductIds = (productDetailContext) => {
  const {
    selectionStateByProductIndex,
    product: { masterProductId },
  } = productDetailContext;
  const bundleId = masterProductId;
  const bundleProductIds = selectionStateByProductIndex.map(
    ({ selectedSKU }) => selectedSKU.productId
  );
  return {
    bundleId,
    bundleProductIds,
  };
};

const getBundleItemProductId = (productDetailContext, bundleItemId) => {
  const { selectionStateByProductIndex } = productDetailContext;

  const bundleItem = selectionStateByProductIndex.find(
    ({ selectedSKU }) => selectedSKU.masterProductId === bundleItemId
  );

  return bundleItem?.selectedSKU?.productId;
};

const isWaitlistProduct = (productDetailContext) => {
  const { isBundle, bundleProducts, product } = productDetailContext;

  if (product.isWaitlistEligible) {
    return true;
  }

  if (isBundle) {
    return bundleProducts.some(({ isWaitlistEligible }) => isWaitlistEligible);
  }

  return false;
};

// Naive error check, returns `true` if there is any SKU doesn't have availability or if a SKU isn't selected yet
// (i.e. not all options selected) or is forced sold out
const getHasError = (productDetailContext, isVip, isFemale) => {
  const { selectionStateByProductIndex, product } = productDetailContext;
  const isMysteryBox = product?.isMysteryBundle;
  // Mystery Box items handle errors internally and do not use selectedSKU
  if (isMysteryBox) {
    return false;
  } else {
    return Object.values(selectionStateByProductIndex).some(
      ({ selectedSKU }) => {
        return selectedSKU
          ? selectedSKU.availableQuantity <= 0 ||
              isForcedSoldOut(product, isVip, isFemale)
          : true;
      }
    );
  }
};

// Returns `true` if a Size isn't selected yet
const getHasSizeSelectorError = (selectedSizeOption) => {
  return !selectedSizeOption;
};

const getHasPreorderError = (productDetailContext) => {
  const { selectionStateByProductIndex } = productDetailContext;
  return !selectionStateByProductIndex.some(({ selectedSKU }) => {
    return selectedSKU
      ? selectedSKU.isPreorder && selectedSKU.availableQuantityPreorder > 0
      : false;
  });
};

const getHasWaitlistError = (productDetailContext) => {
  const { selectionStateByProductIndex } = productDetailContext;
  return Object.values(selectionStateByProductIndex).some(({ selectedSKU }) => {
    return selectedSKU
      ? selectedSKU.availableQuantity <= 0 &&
          !isWaitlistProduct(productDetailContext)
      : true;
  });
};

const subscriptionSelectedKey = 'subscription_selected';

const defaultBOPSStatus = {
  isAvailable: false,
  quantity: 0,
  stockStatus: '',
};

function ProductDetailCTAContext({
  children,
  onSuccessfulAddToCart,
  isPDP,
  isAssortmentTestProductDisabled = false,
  showVipOptInMessage = true,
}) {
  const [optInRadioState, setOptInRadioState] = useState({
    selected: 'inputVipMembershipOptIn',
    inputGuestMembershipOptIn: 'inputGuestMembershipOptIn',
    inputVipMembershipOptIn: 'inputVipMembershipOptIn',
  });

  const { isLoggedIn } = useSession();
  const { query } = useRouter();
  const { isVip } = useMembership();
  const { isFemale } = useGender();
  const { tld } = useDomain();
  const { value: subscriptionSelected } = useSessionDetail(
    subscriptionSelectedKey
  );
  const isUS = tld === '.com';
  const productDetailContext = useProductDetailContext();
  const { scrollToNextError, isScrolling, setIsScrolling } = useErrorContext();
  const cartActions = useCartActions();
  const accountActions = useAccountActions();
  const cart = useCart({ isLoginRequired: true });
  const sessionActions = useSessionActions();
  const {
    isBundle,
    product: { tokenRedemptionValue, tagIdList },
    isQuickView,
  } = productDetailContext;
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorIcon, setErrorIcon] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const { productEmbroidery, bundleItemProductId } =
    useProductDetailEmbroideryContext();
  const { productIndex } = useProductContext();
  const { selectedAttributes } = useProductDetailSelectionState(productIndex);
  const sizeField = useProductAttributeFieldType(AttributeFieldTypes.SIZE);
  const selectedSizeOption = selectedAttributes?.[sizeField];

  const hasError = useMemo(() => {
    return getHasError(productDetailContext, isVip, isFemale);
  }, [isFemale, isVip, productDetailContext]);

  const hasSizeSelectorError = useMemo(() => {
    return getHasSizeSelectorError(selectedSizeOption);
  }, [selectedSizeOption]);

  const hasWaitlistError = useMemo(() => {
    return getHasWaitlistError(productDetailContext);
  }, [productDetailContext]);

  const preorderError = useMemo(() => {
    return getHasPreorderError(productDetailContext);
  }, [productDetailContext]);

  const [signupCartStatus, setSignupCartStatus] = useState(
    SignupMemberships.GUEST
  );
  const [isModalOpen, setOpenModal] = useState(false);

  const closeModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const signupAsVIPCart = useCallback(() => {
    setSignupCartStatus(SignupMemberships.VIP);
  }, []);

  const signupAsGuestCart = useCallback(() => {
    setSignupCartStatus(SignupMemberships.GUEST);
  }, []);

  const dispatch = useDispatch();
  const { psrc: quickViewPsrc } = useQuickViewContext() || {};
  const productSource = query.psrc || quickViewPsrc || '';
  const cartId = cart.cartId || '';

  // BOPS
  const customer = useCustomer();
  const localStorageKey = `pinned_retail_store_${customer.customerKey}`;
  const savedBOPSStore = useMemo(
    () => getLocalStorageItemWithExpiry(localStorageKey),
    [localStorageKey]
  );
  const isSizeSelected = !hasSizeSelectorError;
  const isOutOfStock = hasError && isSizeSelected;
  const { storeLocation } = useStoreLocation();
  const homeStore = storeLocation || null;
  const [pinnedStore, setPinnedStore] = useState(savedBOPSStore);
  const [currentShippingMethod, setCurrentShippingMethod] = useState(
    ShippingMethods.ONLINE
  );
  const [isOpenBOPSErrorModal, setIsOpenBOPSErrorModal] = useState(false);
  const [differentStoreCartItems, setDifferentStoreCartItems] = useState([]);
  const { formatMessage } = useIntl();
  const addToCartSessionDetailName = formatMessage({
    id: 'global_cta.pdp_atb_detail_record',
    defaultMessage: 'pdp_atb',
  });

  const closeBOPSErrorModal = useCallback(() => {
    setIsOpenBOPSErrorModal(false);
  }, []);

  const BOPSItem = useMemo(() => {
    let BOPSStatus = defaultBOPSStatus;
    if (
      pinnedStore &&
      isSizeSelected &&
      isLoggedIn &&
      isVip &&
      isUS &&
      !isBundle &&
      !isQuickView
    ) {
      BOPSStatus = getWarehouseInventory(productDetailContext, pinnedStore);
      if (BOPSStatus.isAvailable === true) {
        setCurrentShippingMethod(ShippingMethods.BOPS);
      }
    }
    return BOPSStatus;
  }, [
    productDetailContext,
    pinnedStore,
    isSizeSelected,
    setCurrentShippingMethod,
    isLoggedIn,
    isVip,
    isUS,
    isBundle,
    isQuickView,
  ]);

  const updateShippingMethod = useCallback((event) => {
    setCurrentShippingMethod(event.target.value);
  }, []);

  // This only handles FL/YT Opt-out of VIP cart
  const updateCartStatus = useCallback(async () => {
    if (!vipCartViaMembershipProduct) {
      if (signupCartStatus !== SignupMemberships.VIP) {
        await cartActions.updateVIPCartStatus({ isVIPCart: false });
      }
    }
  }, [cartActions, signupCartStatus]);

  const { sizeSelections, styleSelections, sizeQuestions, styleQuestions } =
    useMysteryBoxSelectionsContext();

  const addMysteryBoxToCart = useCallback(async () => {
    try {
      setErrorMessage(null);
      const { masterProductId, productSource } = productDetailContext.product;
      const preferences = [
        ...Object.entries(sizeSelections).map(([typeId, label]) => {
          const question = sizeQuestions.find(
            (q) => String(q.mysteryBoxPreferenceTypeId) === typeId
          );
          const answer = question?.answers.find((a) => a.label === label);
          return {
            mysteryBoxPreferenceTypeId: Number(typeId),
            questionType: 'size',
            value: answer ? answer.value.map((v) => v.value).join(',') : label,
          };
        }),
        ...styleQuestions.map((question) => {
          const questionId = String(question.mysteryBoxPreferenceTypeId);
          const selected = styleSelections[questionId];
          // default to "Surprise Me" if no selection, which is the first answer
          const value = selected
            ? selected
                .flatMap((label) => {
                  const answer = question.answers.find(
                    (a) => a.label === label
                  );
                  return answer ? answer.value.map((v) => v.value) : [label];
                })
                .join(',')
            : question.answers?.[0]?.value?.map((v) => v.value).join(',');
          return {
            mysteryBoxPreferenceTypeId: question.mysteryBoxPreferenceTypeId,
            questionType: 'style',
            value,
          };
        }),
      ];
      const payload = {
        masterProductId,
        preferences,
        ...(productSource && { productSource }),
      };
      const response = await cartActions.addMysteryBoxToCart(payload);
      if (response?.payload?.exceptions?.length > 0) {
        setErrorMessage(formatMessage(errorMessages.generalError));
      }
    } catch (error) {
      setErrorMessage(formatMessage(errorMessages.generalError));
      throw error;
    }
  }, [
    productDetailContext.product,
    sizeSelections,
    sizeQuestions,
    styleSelections,
    styleQuestions,
    cartActions,
    formatMessage,
  ]);

  const addToCart = useCallback(
    async (
      options = { applyMemberCredit: false, isRewardItem: false },
      apiParams = {}
    ) => {
      const { enableVIPCart, applyMemberCredit, isRewardItem, psrc } = options;
      const effectivePsrc = psrc || productSource;
      sessionActions.updateSessionDetail({
        name: addToCartSessionDetailName,
        value: 'react',
      });

      // eslint-disable-next-line no-useless-catch
      try {
        let switchToVipCart = false;
        if (
          !isVip &&
          (enableVIPCart ||
            (optInRadioState.selected === 'inputGuestMembershipOptIn' &&
              signupCartStatus === SignupMemberships.VIP))
        ) {
          switchToVipCart = true;
        }
        const additionalOptions = {
          quantity: productDetailContext.itemQuantity,
          switchToVipCart,
          ...apiParams,
          ...(effectivePsrc && {
            productSource: effectivePsrc,
          }),
          ...(applyMemberCredit && {
            tokenQuantity: tokenRedemptionValue,
          }),
        };

        if (productEmbroidery && productEmbroidery.length) {
          if (productDetailContext.isBundle) {
            additionalOptions.bundledComponents = [
              {
                componentProductId: getBundleItemProductId(
                  productDetailContext,
                  bundleItemProductId
                ),
                personalizations: productEmbroidery,
              },
            ];
          } else {
            additionalOptions.personalizations = productEmbroidery;
          }
        }

        if (
          currentShippingMethod === ShippingMethods.BOPS &&
          pinnedStore?.storeId &&
          BOPSItem.isAvailable
        ) {
          additionalOptions.storeId = pinnedStore.storeId;
          additionalOptions.isBops = true;
        }

        let response;

        // API calls are different for adding to cart as a bundle vs individual item.
        if (isBundle) {
          const { bundleId, bundleProductIds } =
            getBundleProductIds(productDetailContext);
          response = await cartActions.addBundleToCart(
            bundleId,
            bundleProductIds,
            additionalOptions
          );
        } else {
          const productId = getProductId(productDetailContext);
          if (isRewardItem) {
            response = await cartActions.addLoyaltyItemToCart(productId, {
              quantity: productDetailContext.itemQuantity,
            });
          } else {
            response = await cartActions.addItemToCart(
              productId,
              additionalOptions
            );
          }
        }

        if (
          !isVip &&
          optInRadioState.selected === 'inputGuestMembershipOptIn'
        ) {
          updateCartStatus();
        }

        // Opt-in/opt-out VIP cart for FL/YT, this happens when clicking on one of the membership select CTAs
        // This needs to happen after any add to cart requests; the `updateVIPCartStatus` won't
        // do anything until the cart is initialized.
        if (typeof enableVIPCart !== 'undefined') {
          if (cart.isVipCart !== enableVIPCart) {
            if (!vipCartViaMembershipProduct && !enableVIPCart) {
              await cartActions.updateVIPCartStatus({
                isVIPCart: enableVIPCart,
              });
            }
          }
        }

        if (!subscriptionSelected) {
          sessionActions.updateSessionDetail({
            name: subscriptionSelectedKey,
            value: true,
          });
        }

        dispatch(
          trackProductAdded({ cartId, ...productDetailContext, productSource })
        );

        return response;
        // eslint-disable-next-line no-useless-catch
      } catch (error) {
        const statusCode = error.statusCode;
        if (statusCode === 409) {
          setErrorMessage(formatMessage(errorMessages.blacklistedWordError));
        } else {
          setErrorMessage(formatMessage(errorMessages.generalError));
        }
        dispatch(
          trackAddToCartError({
            cartId,
            ...productDetailContext,
            productSource,
            error:
              error.errorCode ||
              error.statusCode ||
              error.message ||
              'Unknown error',
          })
        );
        throw error;
      }
    },
    [
      sessionActions,
      addToCartSessionDetailName,
      productDetailContext,
      tokenRedemptionValue,
      productEmbroidery,
      currentShippingMethod,
      pinnedStore,
      BOPSItem.isAvailable,
      isBundle,
      isVip,
      optInRadioState.selected,
      subscriptionSelected,
      dispatch,
      cartId,
      productSource,
      bundleItemProductId,
      cartActions,
      updateCartStatus,
      cart.isVipCart,
      formatMessage,
      signupCartStatus,
    ]
  );

  const addToWaitlist = useCallback(
    async ({ apiParams = {} }) => {
      // eslint-disable-next-line no-useless-catch
      try {
        sessionActions.updateSessionDetail({
          name: addToCartSessionDetailName,
          value: 'react',
        });
        if (isBundle) {
          const { bundleId, bundleProductIds } =
            getBundleProductIds(productDetailContext);
          await accountActions.addWaitlistBundle(
            bundleId,
            bundleProductIds,
            apiParams
          );
        } else {
          const productId = getProductId(productDetailContext);
          await accountActions.addWaitlistItem(productId, apiParams);
        }
      } catch (error) {
        setErrorMessage(formatMessage(errorMessages.generalError));
        throw error;
      }
    },
    [
      accountActions,
      addToCartSessionDetailName,
      formatMessage,
      isBundle,
      productDetailContext,
      sessionActions,
    ]
  );

  const updateSelectedStore = useCallback(
    (store) => {
      if (
        !pinnedStore ||
        (pinnedStore && pinnedStore.storeId !== store.storeId)
      ) {
        // expire key for 2 hours
        const duration = 1000 * 60 * 60 * 2;
        setLocalStorageItemWithExpiry(localStorageKey, store, duration);
        setPinnedStore(store);
      }
    },
    [pinnedStore, localStorageKey]
  );

  const checkValidStoreForBOPS = useCallback(() => {
    const { items } = cart;
    let isDifferentStore = false;
    const stores = [];
    items.forEach((cartItem) => {
      if (cartItem.storeId && cartItem.storeId !== pinnedStore.storeId) {
        isDifferentStore = true;
        stores.push(cartItem);
      }
    });
    setDifferentStoreCartItems(stores);
    return isDifferentStore;
  }, [cart, pinnedStore]);

  const deleteFromCart = useCallback(
    async (cartItem) => {
      // ECHO has unified cart remove
      const isBundle = Boolean(
        cartItem.bundleItems && cartItem.bundleItems.length
      );
      if (isEchoCartLineId(cartItem.cartLineId)) {
        await cartActions.removeCartLineItem(cartItem.cartLineId);
      } else if (isBundle) {
        await cartActions.removeBundleFromCart(cartItem.groupKey);
      } else {
        await cartActions.removeCartLineItem(cartItem.lineId);
      }
    },
    [cartActions]
  );

  const updateCartItems = useCallback(async () => {
    const removeActions = differentStoreCartItems.map((cartItem) =>
      cartActions.removeCartLineItem(cartItem.cartLineId)
    );
    await Promise.all(removeActions);
    await addToCart();
    closeBOPSErrorModal();
  }, [cartActions, addToCart, closeBOPSErrorModal, differentStoreCartItems]);

  const addToCartForSpeedySignup = useCallback(
    async (psrc) => {
      const effectivePsrc = psrc || productSource;
      try {
        sessionActions.updateSessionDetail({
          name: addToCartSessionDetailName,
          value: 'react',
        });
        const switchToVipCart = signupCartStatus === SignupMemberships.VIP;
        const additionalOptions = {
          quantity: productDetailContext.itemQuantity,
          switchToVipCart,
          ...(effectivePsrc && {
            productSource: effectivePsrc,
          }),
        };
        let response;

        if (productEmbroidery && productEmbroidery.length) {
          if (productDetailContext.isBundle) {
            additionalOptions.bundledComponents = [
              {
                componentProductId: getBundleItemProductId(
                  productDetailContext,
                  bundleItemProductId
                ),
                personalizations: productEmbroidery,
              },
            ];
          } else {
            additionalOptions.personalizations = productEmbroidery;
          }
        }

        // API calls are different for adding to cart as a bundle vs individual item.
        if (isBundle) {
          const { bundleId, bundleProductIds } =
            getBundleProductIds(productDetailContext);
          response = await cartActions.addBundleToCart(
            bundleId,
            bundleProductIds,
            additionalOptions
          );
          await updateCartStatus();
        } else {
          const productId = getProductId(productDetailContext);
          response = await cartActions.addItemToCart(
            productId,
            additionalOptions
          );
          await updateCartStatus();
        }

        if (!subscriptionSelected) {
          sessionActions.updateSessionDetail({
            name: subscriptionSelectedKey,
            value: true,
          });
        }

        dispatch(
          trackProductAdded({
            cartId,
            ...productDetailContext,
            productSource: effectivePsrc,
          })
        );

        return response;
      } catch (error) {
        setErrorMessage(formatMessage(errorMessages.generalError));
        dispatch(
          trackAddToCartError({
            cartId,
            ...productDetailContext,
            productSource: effectivePsrc,
            error:
              error.errorCode ||
              error.statusCode ||
              error.message ||
              'Unknown error',
          })
        );
        throw error;
      }
    },
    [
      sessionActions,
      addToCartSessionDetailName,
      productDetailContext,
      productSource,
      isBundle,
      productEmbroidery,
      subscriptionSelected,
      dispatch,
      cartId,
      cartActions,
      updateCartStatus,
      bundleItemProductId,
      formatMessage,
      signupCartStatus,
    ]
  );

  useEffect(() => {
    if (isOutOfStock && !showErrors) {
      setShowErrors(true);
    }
  }, [isOutOfStock, showErrors]);

  const handleSharedValidation = useCallback(async () => {
    setErrorIcon(true);
    setErrorMessage(null);
    if (!showErrors) {
      setShowErrors(true);
    }
    if (hasError) {
      if (isScrolling) {
        scrollToNextError();
      } else {
        setIsScrolling(true);
      }
      throw new Error(ProductDetailCTAErrorTypes.SelectorError);
    } else {
      if (!isLoggedIn) {
        sessionActions.updateSessionDetail({
          name: 'ss_start',
          value: '',
        });
        setOpenModal(true);
        throw new Error(ProductDetailCTAErrorTypes.NotLoggedInError);
      } else {
        // throw error is another store is selected
        if (
          BOPSItem.isAvailable &&
          currentShippingMethod === ShippingMethods.BOPS
        ) {
          if (checkValidStoreForBOPS()) {
            setIsOpenBOPSErrorModal(true);
            throw new Error(ProductDetailCTAErrorTypes.BOPSDifferentStoreError);
          }
        }
        return Promise.resolve();
      }
    }
  }, [
    hasError,
    isLoggedIn,
    isScrolling,
    scrollToNextError,
    setIsScrolling,
    setOpenModal,
    setShowErrors,
    showErrors,
    BOPSItem,
    currentShippingMethod,
    checkValidStoreForBOPS,
    sessionActions,
  ]);

  const handlePreorderValidation = useCallback(async () => {
    const hasError = getHasPreorderError(productDetailContext);
    if (hasError) {
      throw new Error(ProductDetailCTAErrorTypes.SelectorError);
    } else {
      return Promise.resolve();
    }
  }, [productDetailContext]);

  const handleWaitlistValidation = useCallback(() => {
    if (hasWaitlistError) {
      throw new Error(ProductDetailCTAErrorTypes.SelectorError);
    } else {
      return Promise.resolve();
    }
  }, [hasWaitlistError]);

  const isPreorderProduct = useMemo(() => {
    const { product, selectionStateByProductIndex } = productDetailContext;

    return isPreorderEligibleBySelectedProduct(
      product,
      selectionStateByProductIndex
    );
  }, [productDetailContext]);

  const { isLoyaltyItem } = useLoyaltyPricing(productDetailContext.product);

  const isPreorderEligible = isPreorderProduct && isUS;
  const isWaitlistEligible = isVip && isWaitlistProduct(productDetailContext);
  const isDualCTAsEnabled =
    (!isVip && !subscriptionSelected) ||
    (subscriptionSelected && !cart.isVipCart) ||
    (!cart.isVipCart && !isPreorderEligible && !isWaitlistEligible) ||
    (!isVip && isPreorderEligible && isWaitlistEligible);
  const isVIPAddToBagEnabled =
    (!isVip && subscriptionSelected && cart.isVipCart) ||
    (isVip && !isPreorderEligible && !isWaitlistEligible) ||
    (!isVip && isPreorderEligible && isWaitlistEligible);

  const { isByoQualifiedProduct, offerTypeKey, offerTypePlainText } =
    useByoConfigForProduct({
      productFplGlobalCodes:
        productDetailContext.product.activeFeaturedProductLocationCodeList,
      productFplIds:
        productDetailContext.product.activeFeaturedProductLocationIdList,
    });

  /**
   * Products tagged with this tag will disable the CTAs on the PDP with
   * no additional messaging. Currently this is used for products that are
   * hidden from grid, but may be available for GWP. We only disable the CTAs
   * on PDP since these products should still be able to be added to cart via
   * the GWP flow. (not currently in `web-spa`, this was set up defensively
   * for GWP implementation in the future)
   */
  const isForceDisableCtaEnabled = useMemo(() => {
    if (isPDP && tagIdList) {
      return getBrandValue({
        default: false,
        savage: tagIdList.includes(ProductTag.forceDisablePdpCta),
        fabletics: tagIdList.includes(ProductTag.forceDisablePdpCta),
      });
    }

    return false;
  }, [isPDP, tagIdList]);

  const contextValue = useMemo(
    () => ({
      addToCart,
      addToCartForSpeedySignup,
      addMysteryBoxToCart,
      addToWaitlist,
      closeModal,
      getBundleProductIds,
      getProductId,
      handlePreorderValidation,
      handleSharedValidation,
      handleWaitlistValidation,
      hasError,
      hasSizeSelectorError,
      showErrors,
      hasWaitlistError,
      isAssortmentTestProductDisabled,
      isBundle,
      isDualCTAsEnabled,
      isForceDisableCtaEnabled,
      isLoading: cart.networkStatus.isLoading,
      isModalOpen,
      isOutOfStock,
      isPreorderEligible,
      isVIPAddToBagEnabled,
      isWaitlistProduct,
      isLoyaltyItem,
      BOPSItem,
      currentShippingMethod,
      defaultStore: pinnedStore || homeStore || null,
      homeStore,
      hasPinnedStore: Boolean(pinnedStore),
      isSizeSelected,
      pinnedStore,
      ShippingMethods,
      setDefaultShippingMethod: setCurrentShippingMethod,
      storeLocation,
      updateSelectedStore,
      updateShippingMethod,
      isOpenBOPSErrorModal,
      closeBOPSErrorModal,
      updateCartItems,
      isWaitlistEligible,
      preorderError,
      showVipOptInMessage,
      signupAsVIPCart,
      signupAsGuestCart,
      signupCartStatus,
      onSuccessfulAddToCart,
      deleteFromCart,
      errorMessage,
      setErrorMessage,
      errorIcon,
      setErrorIcon,
      optInRadioState,
      setOptInRadioState,
      isByoQualifiedProduct,
      offerTypeKey,
      offerTypePlainText,
    }),
    [
      addToCart,
      addToCartForSpeedySignup,
      addMysteryBoxToCart,
      addToWaitlist,
      closeModal,
      handlePreorderValidation,
      handleSharedValidation,
      handleWaitlistValidation,
      hasError,
      hasSizeSelectorError,
      showErrors,
      hasWaitlistError,
      isAssortmentTestProductDisabled,
      isBundle,
      isDualCTAsEnabled,
      isForceDisableCtaEnabled,
      cart.networkStatus.isLoading,
      isModalOpen,
      isOutOfStock,
      isPreorderEligible,
      isVIPAddToBagEnabled,
      isLoyaltyItem,
      BOPSItem,
      currentShippingMethod,
      pinnedStore,
      homeStore,
      isSizeSelected,
      storeLocation,
      updateSelectedStore,
      updateShippingMethod,
      isOpenBOPSErrorModal,
      closeBOPSErrorModal,
      updateCartItems,
      isWaitlistEligible,
      preorderError,
      showVipOptInMessage,
      signupAsVIPCart,
      signupAsGuestCart,
      signupCartStatus,
      onSuccessfulAddToCart,
      deleteFromCart,
      errorMessage,
      errorIcon,
      optInRadioState,
      isByoQualifiedProduct,
      offerTypeKey,
      offerTypePlainText,
    ]
  );

  return (
    <PDPCTAContext.Provider value={contextValue}>
      {typeof children === 'function' ? children(contextValue) : children}
    </PDPCTAContext.Provider>
  );
}

ProductDetailCTAContext.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isAssortmentTestProductDisabled: PropTypes.bool,
  isPDP: PropTypes.bool,
  onSuccessfulAddToCart: PropTypes.func,
  showVipOptInMessage: PropTypes.bool,
};

export default ProductDetailCTAContext;
